import React, { useState } from 'react';
import clsx from 'clsx';

import lock from 'assets/images/ic_lock_white.svg';
import { useFloatingTooltip } from 'hooks/useFloatingTooltip';
import { useAutosizeTextarea } from 'hooks/useAutosizeTextarea';
import DeleteButton from 'components/Button/DeleteButton';
import FormCheckbox from 'components/FormFields/FormCheckbox';

import './booking-process.scss';

const Card = (props) => {
  const {
    id,
    name,
    current,
    isEditable,
    isSortable,
    updateStatus,
    deleteStatus,
    isNew,
    setNodeRef,
    attributes,
    listeners,
    style,
    isDragging,
  } = props || {};
  const [label, setLabel] = useState(name);
  const [isCurrent, setIsCurrent] = useState(current);
  const { textareaRef } = useAutosizeTextarea({ enterToSubmit: true });

  // Need to remove keydown listener when user is typing, because keydown
  // listener removes the ability to add spaces in textarea.
  // Note: This doesn't handle when this is used in a web component. Added
  // extra onKeyDown function to handle when this is in a shadow dom.
  const { onPointerDown, onMouseDown, onTouchStart, onKeyDown } = listeners || {};
  const textareaActive = textareaRef.current === document.activeElement;

  const {
    showTooltip: showCheckboxTooltip,
    hideTooltip: hideCheckboxTooltip,
    updateTooltip: updateCheckboxTooltip,
    tooltipElement: checkboxTooltipElement,
  } = useFloatingTooltip('Mark as Current');

  const {
    showTooltip: showDeleteTooltip,
    hideTooltip: hideDeleteTooltip,
    updateTooltip: updateDeleteTooltip,
    tooltipElement: deleteTooltipElement,
  } = useFloatingTooltip('Delete status');

  const onCheckboxChange = () => {
    const changedValue = !isCurrent;
    updateStatus({ statusId: id, current: changedValue });
    setIsCurrent(changedValue);
  };

  const onSaveInput = () => {
    const words = label.split(' ');
    const changedWords = words.map(w => w.charAt(0).toUpperCase() + w.slice(1));
    updateStatus({ statusId: id, name: changedWords.join(' ') });
  };

  // This handles when this component is rendered in a shadow dom.
  const handleTextareaKeydown = (e) => {
    // Prevent bubbling to dnd kit when spacebar is pressed.
    if (e.code === 'Space') {
      e.stopPropagation();
    }
  };

  return (
    <>
      <div
        key={`status-${id}`}
        ref={setNodeRef}
        style={style}
        onPointerDown={onPointerDown}
        onMouseDown={onMouseDown}
        onTouchStart={onTouchStart}
        onKeyDown={textareaActive ? undefined : onKeyDown}
        {...attributes}
        className={clsx('booking-process__status', 'status-card',
          !isEditable && 'locked', !isSortable && 'unsortable', isDragging && 'dragging')}
      >
        <div className="table-status__card-container">
          <div
            className={clsx('filter-cards__card', {
              'selected': true,
            })}
            title=""
          >
            {isEditable ? (
              <>
                <FormCheckbox
                  entityId={id}
                  name="Mark as Current"
                  wrapperProps={{
                    className: 'status-card__indicator small-checkbox',
                  }}
                  labelProps={{
                    onMouseEnter: (e) => {
                      showCheckboxTooltip();
                      updateCheckboxTooltip(e);
                    },
                    onMouseMove: (e) => updateCheckboxTooltip(e),
                    onMouseLeave: hideCheckboxTooltip,
                  }}
                  checked={isCurrent}
                  onChange={onCheckboxChange}
                />
                <DeleteButton
                  className="status-card__delete"
                  onClick={() => deleteStatus(id)}
                  onMouseEnter={e => {
                    showDeleteTooltip();
                    updateDeleteTooltip(e);
                  }}
                  onMouseMove={e => updateDeleteTooltip(e)}
                  onMouseLeave={hideDeleteTooltip}
                />
              </>
            ) : (
              <img
                src={lock}
                alt="lock"
                className="status-card__indicator status-card__indicator--lock"
              />
            )}
            <textarea
              ref={textareaRef}
              className="ap-text status-card__name"
              defaultValue={name}
              placeholder="Add new status"
              rows="1"
              maxLength="52"
              disabled={!isEditable || isDragging}
              onKeyDown={handleTextareaKeydown}
              onChange={(e) => setLabel(e.target.value)}
              onBlur={onSaveInput}
              autoFocus={isNew}
            />
          </div>
        </div>
      </div>
      {checkboxTooltipElement}
      {deleteTooltipElement}
    </>
  );
};

export default Card;
